import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import parse from 'html-react-parser';
import { Tabs } from 'antd';
import type { TabsProps } from 'antd';
import { useModal } from '@/hooks';
import {
  Button,
  ChangePassword,
  CheckMethodModal,
  CriticalBalanceAlert,
  FieldInfoGroup,
  SectionBox,
  ShareBalance,
  TurnOff2FA,
  TurnOn2FA,
} from '@/components';

import { EmailStatusEnum } from '@/api';
import {
  modalSelectors,
  profileSelectors,
  userSelectors,
} from '@/redux/selectors';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  profileActions,
  promoHistoryActions,
  twoFAActivateModalActions,
  twoFADeActivateModalActions,
  criticalBalanceAlertModalActions,
  shareBalanceModalActions,
  checkMethodModalActions,
  receiveRemindersModalActions,
} from '@/redux/actions';

import { AuthProviderEnum } from '@/config';
import {
  ApiKeys,
  PersonalInformation,
  PromoCodeHistory,
  ReferralProgram,
  YourBonus,
  ReceiveRemindersModal,
} from './components';
import { RiskscoreSettings } from './components/RiskscoreSettings';
import styles from './Profile.module.sass';

interface ProfileProps {}

const Profile: FC<ProfileProps> = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { data: userData } = useAppSelector(userSelectors.getUserSlice);
  const { data: profileData, loading } = useAppSelector(
    profileSelectors.getProfileSlice,
  );
  const hasCustomRiskProfile = useAppSelector(
    profileSelectors.hasRiskscoreProfile,
  );
  const isAccountLiteMode = useAppSelector(profileSelectors.isAccountLiteMode);
  const [showPasswordBlock, setShowPasswordBlock] = useState(false);
  const [showBalanceAlertsBlock, setShowBalanceAlertsBlock] = useState(false);
  const isEmailConfirmed = useAppSelector(userSelectors.checkIfEmailConfirmed);

  const show2FABlock = userData?.is2faAllowed;
  const balanceAlertConfigured = !!profileData?.balanceAlertsSets?.status;

  useEffect(() => {
    if (
      userData?.emailStatus === EmailStatusEnum.CONFIRMED &&
      (userData?.provider === AuthProviderEnum.DEFAULT ||
        userData?.provider === AuthProviderEnum.GOOGLE)
    ) {
      setShowPasswordBlock(true);
    }

    // if (userData?.provider !== AuthProviderEnum.TWITTER) {
    //   setShowBalanceAlertsBlock(true);
    // }
  }, []);

  useEffect(() => {
    dispatch(profileActions.getProfileInfo());
    dispatch(promoHistoryActions.fetchPromoHistory({}));
  }, []);

  const {
    isOpen: isOpenChangePassword,
    onOpenModal: openChangePassword,
    onCloseModal: closeChangePassword,
  } = useModal();

  const twoFAActivateModalSlice = useAppSelector(
    modalSelectors.getTwoFAActivateModalSlice,
  );

  const twoFADeActivateModalSlice = useAppSelector(
    modalSelectors.getTwoFADeActivateModalSlice,
  );

  const criticalBalanceAlertModalSlice = useAppSelector(
    modalSelectors.getCriticalBalanceAlertModalSlice,
  );

  const shareBalanceModalSlice = useAppSelector(
    modalSelectors.getShareBalanceModalSlice,
  );

  const checkMethodModalSlice = useAppSelector(
    modalSelectors.getCheckMethodModalSlice,
  );

  const receiveRemindersModalSlice = useAppSelector(
    modalSelectors.getReceiveRemindersModalSlice,
  );

  const presetedFlowModes = useAppSelector(profileSelectors.presetedFlowModes);

  const canChangeCheckFlow = useMemo(() => {
    if (isAccountLiteMode) {
      return false;
    }

    if (Array.isArray(presetedFlowModes)) {
      return presetedFlowModes.length === 0
        ? true
        : presetedFlowModes.length > 1;
    }

    return true;
  }, [profileData?.preferences]);

  const twoFAHandler = () => {
    if (userData?.use2Fa) {
      dispatch(twoFADeActivateModalActions.openModal());
    } else {
      dispatch(twoFAActivateModalActions.openModal());
    }
  };

  const twoFAButtonText = userData?.use2Fa
    ? t('translation.2fa_off_btn')
    : t('translation.2fa_on_btn');
  const securitySectionTitle = t('translation.profile_security_title');
  const receiveRemindersButtonText = userData?.receiveReminders
    ? t('translation.reminders_off_btn')
    : t('translation.reminders_on_btn');

  const tabs: TabsProps['items'] = [
    {
      key: '1',
      label: t('translation.profile'),
      children: (
        <>
          <PersonalInformation />
          <SectionBox>
            {{
              body: (
                <>
                  <FieldInfoGroup
                    title={t('translation.amlflow_title')}
                    actions={
                      canChangeCheckFlow && (
                        <Button
                          type="secondary"
                          corner="rect"
                          disabled={loading}
                          className={styles.actionButton}
                          onClick={() =>
                            dispatch(checkMethodModalActions.openModal())
                          }
                        >
                          {t('translation.set_amlflow_link')}
                        </Button>
                      )
                    }
                  />
                  <p>
                    <b>{t(`translation.${userData?.amlFlow}_flow_str`)}</b>
                  </p>
                  <p>
                    <small>
                      {parse(
                        t(`translation.amlflow_${userData?.amlFlow}_details`),
                      )}
                    </small>
                  </p>
                </>
              ),
            }}
          </SectionBox>
          <YourBonus />
          <SectionBox>
            {{
              body: (
                <FieldInfoGroup
                  title={t('translation.reminder_section_title')}
                  actions={
                    <Button
                      type="secondary-red"
                      corner="rect"
                      className={styles.actionButton}
                      disabled={loading}
                      onClick={() =>
                        dispatch(receiveRemindersModalActions.openModal())
                      }
                    >
                      <span>{receiveRemindersButtonText}</span>
                    </Button>
                  }
                />
              ),
            }}
          </SectionBox>
        </>
      ),
    },
    ...(userData?.refProgram
      ? [
          {
            key: '2',
            label: t('translation.referral_program_title'),
            children: <ReferralProgram />,
          },
        ]
      : []),
    {
      key: '3',
      label: securitySectionTitle,
      children: (
        <SectionBox>
          {{
            body: (
              <>
                {showPasswordBlock && (
                  <FieldInfoGroup
                    title={t('translation.change_pwd_title')}
                    description="••••••••••"
                    actions={
                      <Button
                        type="secondary"
                        corner="rect"
                        className={styles.actionButton}
                        disabled={loading}
                        onClick={openChangePassword}
                      >
                        {t('translation.change_pwd_link')}
                      </Button>
                    }
                  />
                )}

                {show2FABlock && (
                  <FieldInfoGroup
                    title={t('translation.2fa_title')}
                    description={t('translation.2fa_subtitle')!}
                    actions={
                      <Button
                        type="secondary"
                        corner="rect"
                        className={styles.actionButton}
                        disabled={loading}
                        onClick={twoFAHandler}
                      >
                        {twoFAButtonText}
                      </Button>
                    }
                  />
                )}
              </>
            ),
          }}
        </SectionBox>
      ),
    },
    // {
    //   key: '4',
    //   label: t('translation.user_promo_issue_title'),
    //   children: !userData?.promoBalance && (
    //     <SectionBox>
    //       {{
    //         body: (
    //           <>
    //             <FieldInfoGroup
    //               title={t('translation.user_promo_issue_title')}
    //               description={t('translation.user_promo_issue_description')!}
    //               actions={
    //                 isEmailConfirmed ? (
    //                   <Button
    //                     type="secondary"
    //                     corner="rect"
    //                     className={styles.actionButton}
    //                     disabled={loading}
    //                     onClick={() =>
    //                       dispatch(shareBalanceModalActions.openModal())
    //                     }
    //                   >
    //                     {t('translation.user_promo_modal_title')}
    //                   </Button>
    //                 ) : null
    //               }
    //             />
    //             <PromoCodeHistory />
    //           </>
    //         ),
    //       }}
    //     </SectionBox>
    //   ),
    // },
    ...(isEmailConfirmed
      ? [
          {
            key: '5',
            label: 'API',
            children: <ApiKeys />,
          },
        ]
      : []),
    // {
    //   key: '6',
    //   label: t('translation.billing'),
    //   children: showBalanceAlertsBlock && (
    //     <SectionBox>
    //       {{
    //         title: t('translation.bal_alerts_title_str'),
    //         actions: balanceAlertConfigured && (
    //           <Button
    //             type="secondary-red"
    //             corner="rect"
    //             className={styles.actionButton}
    //             onClick={() => dispatch(profileActions.deleteBalanceAlert())}
    //             disabled={loading}
    //           >
    //             {t('translation.delete_alerts_btn')}
    //           </Button>
    //         ),
    //         body: (
    //           <FieldInfoGroup
    //             description={
    //               balanceAlertConfigured
    //                 ? t('translation.bal_alerts_set_str', {
    //                     count: Number(
    //                       profileData?.balanceAlertsSets?.level || 0,
    //                     ),
    //                     time: !profileData
    //                       ? ''
    //                       : profileData.balanceAlertsPeriods?.[
    //                           profileData.balanceAlertsSets?.period
    //                         ] || '',
    //                   })!
    //                 : t('translation.bal_alerts_notset_str')!
    //             }
    //             actions={
    //               <Button
    //                 type="secondary"
    //                 corner="rect"
    //                 className={styles.actionButton}
    //                 onClick={() =>
    //                   dispatch(criticalBalanceAlertModalActions.openModal())
    //                 }
    //                 disabled={loading}
    //               >
    //                 {balanceAlertConfigured
    //                   ? t('translation.change_alerts_btn')
    //                   : t('translation.set_alerts_btn')}
    //               </Button>
    //             }
    //           />
    //         ),
    //       }}
    //     </SectionBox>
    //   ),
    // },
    ...(hasCustomRiskProfile && isEmailConfirmed
      ? [
          {
            key: '7',
            label: t('translation.riskscore_modal_title'),
            children: <RiskscoreSettings />,
          },
        ]
      : []),
  ];

  return (
    <>
      <div className={styles.profile}>
        <Tabs defaultActiveKey="1" items={tabs} destroyOnHidden />
      </div>

      <ChangePassword
        isOpen={isOpenChangePassword}
        onClose={closeChangePassword}
      />

      <TurnOn2FA
        isOpen={twoFAActivateModalSlice.isOpen}
        onClose={() => dispatch(twoFAActivateModalActions.closeModal())}
      />

      <TurnOff2FA
        isOpen={twoFADeActivateModalSlice.isOpen}
        onClose={() => dispatch(twoFADeActivateModalActions.closeModal())}
      />

      <CriticalBalanceAlert
        isOpen={criticalBalanceAlertModalSlice.isOpen}
        onClose={() => dispatch(criticalBalanceAlertModalActions.closeModal())}
      />

      <CheckMethodModal
        isOpen={checkMethodModalSlice.isOpen}
        onClose={() => dispatch(checkMethodModalActions.closeModal())}
      />

      <ShareBalance
        isOpen={shareBalanceModalSlice.isOpen}
        onClose={() => dispatch(shareBalanceModalActions.closeModal())}
      />
      <ReceiveRemindersModal
        isOpen={receiveRemindersModalSlice.isOpen}
        onClose={() => dispatch(receiveRemindersModalActions.closeModal())}
      />
    </>
  );
};

export default Profile;
